/**
 * Socket 事件管理模块
 * 负责处理 Socket 事件的注册、触发和清理
 */

import type { Socket } from 'socket.io-client'
import { EventPriority, IS_LOG } from './config'
import type { SocketError } from './types'

/**
 * 事件处理器类型
 * 定义事件处理函数的类型
 */
type EventHandler = (...args: any[]) => Promise<void> | void

/**
 * 事件映射接口
 * 用于存储事件处理器及其配置
 */
interface EventMap {
  [key: string]: {
    handler: EventHandler // 事件处理函数
    priority: EventPriority // 事件优先级
    retryCount: number // 重试次数
    maxRetries: number // 最大重试次数
  }[]
}

/**
 * 事件队列项接口
 * 用于存储待处理的事件
 */
interface EventQueueItem {
  event: string // 事件名称
  args: any[] // 事件参数
  timestamp: number // 时间戳
  retryCount: number // 重试次数
}

/**
 * Socket 事件管理器类
 * 提供事件注册、触发、清理等功能
 */
export class SocketEventManager {
  // 事件处理器映射表
  private eventMap: EventMap = {}
  // 事件队列
  private eventQueue: EventQueueItem[] = []
  // 队列处理状态标志
  private isProcessingQueue = false
  // 最大队列大小
  private readonly maxQueueSize = 1000
  // 最大重试延迟时间（毫秒）
  private readonly maxRetryDelay = 5000

  /**
   * 构造函数
   * @param socket Socket 实例
   */
  constructor(private socket: Socket) {
    this.initEventListeners()
  }

  /**
   * 初始化事件监听器
   * 监听连接和断开连接事件
   */
  private initEventListeners() {
    this.socket.on('connect', () => {
      this.processEventQueue()
    })

    this.socket.on('disconnect', () => {
      this.isProcessingQueue = false
    })
  }

  /**
   * 处理事件队列
   * 当连接恢复时，处理队列中的事件
   */
  private async processEventQueue() {
    if (this.isProcessingQueue || this.eventQueue.length === 0)
      return

    this.isProcessingQueue = true
    while (this.eventQueue.length > 0 && this.socket.connected) {
      const item = this.eventQueue.shift()!
      try {
        await this.emit(item.event, ...item.args)
      }
      catch (error) {
        if (item.retryCount < 3) {
          item.retryCount++
          this.eventQueue.unshift(item)
          await new Promise(resolve => setTimeout(resolve, Math.min(1000 * item.retryCount, this.maxRetryDelay)))
        }
      }
    }
    this.isProcessingQueue = false
  }

  /**
   * 注册事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   * @param priority 事件优先级
   * @returns 取消注册的函数
   */
  on(event: string, handler: EventHandler, priority: EventPriority = EventPriority.NORMAL) {
    if (!this.eventMap[event]) {
      this.eventMap[event] = []
    }

    this.eventMap[event].push({
      handler,
      priority,
      retryCount: 0,
      maxRetries: 3,
    })

    // 按优先级排序
    this.eventMap[event].sort((a, b) => b.priority - a.priority)

    return () => this.off(event, handler)
  }

  /**
   * 注册一次性事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   * @param priority 事件优先级
   * @returns 取消注册的函数
   */
  once(event: string, handler: EventHandler, priority: EventPriority = EventPriority.NORMAL) {
    const wrappedHandler = async (...args: any[]) => {
      try {
        await handler(...args)
      }
      finally {
        this.off(event, wrappedHandler)
      }
    }

    return this.on(event, wrappedHandler, priority)
  }

  /**
   * 移除事件监听器
   * @param event 事件名称
   * @param handler 事件处理函数
   */
  off(event: string, handler: EventHandler) {
    if (!this.eventMap[event])
      return

    this.eventMap[event] = this.eventMap[event].filter(item => item.handler !== handler)
  }

  /**
   * 触发事件
   * @param event 事件名称
   * @param args 事件参数
   */
  async emit(event: string, ...args: any[]): Promise<void> {
    if (!this.socket.connected) {
      if (this.eventQueue.length < this.maxQueueSize) {
        this.eventQueue.push({
          event,
          args,
          timestamp: Date.now(),
          retryCount: 0,
        })
      }
      return
    }

    // 先发送事件到服务器
    this.socket.emit(event, ...args)

    // 处理本地事件监听器
    const handlers = this.eventMap[event] || []
    const errors: SocketError[] = []

    // 执行所有事件处理器
    for (const { handler, retryCount, maxRetries } of handlers) {
      try {
        await handler(...args)
      }
      catch (error) {
        const socketError: SocketError = {
          name: 'SocketError',
          message: error instanceof Error ? error.message : 'Unknown error',
          code: 500,
          timestamp: Date.now(),
          recoverable: retryCount < maxRetries,
          details: error,
        }
        errors.push(socketError)

        // 如果可重试，则进行重试
        if (retryCount < maxRetries) {
          setTimeout(async () => {
            try {
              await handler(...args)
            }
            catch (retryError) {
              if (IS_LOG) {
                console.error(`Retry failed for event ${event}:`, retryError)
              }
            }
          }, Math.min(1000 * (retryCount + 1), this.maxRetryDelay))
        }
      }
    }

    // 如果有错误，抛出第一个错误
    if (errors.length > 0) {
      throw errors[0]
    }
  }

  /**
   * 等待事件
   * @param event 事件名称
   * @param callback 回调函数
   * @returns Promise
   */
  waitFor(event: string, callback?: (data: any) => void): Promise<any> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.off(event, handler)
        reject(new Error(`Timeout waiting for event: ${event}`))
      }, 30000)

      const handler = (data: any) => {
        clearTimeout(timeout)
        this.off(event, handler)
        if (callback) {
          callback(data)
        }
        resolve(data)
      }

      this.on(event, handler, EventPriority.HIGH)
    })
  }

  /**
   * 清理所有事件监听器
   */
  clear() {
    this.eventMap = {}
    this.eventQueue = []
    this.isProcessingQueue = false
  }
}
